import React, { useState, useEffect } from 'react';

// MUI
import { styled } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const StyledAutocomplete = styled(Autocomplete)(({ disabled }) => ({
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    top: '-8px !important',
  },
  ...(disabled && {
    '& .MuiInputBase-root': {
      background: 'rgba(248, 248, 248, 1) !important',
    },
  }),
}));

const ConstraintText = styled(Typography)({
  fontSize: '12px',
  color: '#666',
  marginTop: '4px',
});

// Simple color generator based on string
const getColourFromString = (str) => {
  if (!str) return '#1976d2';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#1976d2',
    '#388e3c',
    '#d32f2f',
    '#7b1fa2',
    '#1565c0',
    '#00838f',
    '#ef6c00',
    '#6a1b9a',
  ];
  return colors[Math.abs(hash) % colors.length];
};

function ListInput({ data }) {
  const {
    answers,
    column,
    rowIndex,
    columnIndex,
    updateValue,
    isReadOnly,
    size,
    isRS,
  } = data;

  const [items, setItems] = useState([]);
  const [inputValue, setInputValue] = useState('');

  // Parse stored value (JSON array or comma-separated string) to array
  const parseStoredValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      // Not JSON, try comma-separated
    }
    return value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '');
  };

  useEffect(() => {
    const currentValue = answers[rowIndex][columnIndex].calculatedValue;
    const parsed = parseStoredValue(currentValue);
    setItems(parsed);
  }, []);

  // For deleting a row in repeatable sections
  useEffect(() => {
    if (isRS) {
      const currentValue = answers[rowIndex][columnIndex].calculatedValue;
      const parsed = parseStoredValue(currentValue);
      if (JSON.stringify(parsed) !== JSON.stringify(items)) {
        setItems(parsed);
      }
    }
  }, [answers]);

  const handleChange = (event, newValue) => {
    // Check max constraint
    const listMax = column.listMax ? parseInt(column.listMax) : 0;
    if (listMax > 0 && newValue.length > listMax) {
      return;
    }

    setItems(newValue);
    // Store as JSON array
    updateValue(rowIndex, columnIndex, JSON.stringify(newValue), false);
  };

  const getConstraintMessage = () => {
    const listMin = column.listMin ? parseInt(column.listMin) : 0;
    const listMax = column.listMax ? parseInt(column.listMax) : 0;

    if (listMin > 0 && listMax > 0) {
      return `Enter ${listMin} to ${listMax} items (${items.length} entered)`;
    } else if (listMin > 0) {
      return `Enter at least ${listMin} items (${items.length} entered)`;
    } else if (listMax > 0) {
      return `Enter up to ${listMax} items (${items.length} entered)`;
    }
    return null;
  };

  const constraintMessage = getConstraintMessage();

  return (
    <Box id={`table-row-${rowIndex}-col-${columnIndex}`}>
      <StyledAutocomplete
        multiple
        freeSolo
        options={[]}
        value={items}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          // Handle comma input to add items
          if (newInputValue.includes(',')) {
            const parts = newInputValue.split(',');
            const newItems = parts
              .slice(0, -1)
              .map((p) => p.trim())
              .filter((p) => p !== '');
            if (newItems.length > 0) {
              const listMax = column.listMax ? parseInt(column.listMax) : 0;
              const combined = [...items, ...newItems];
              if (listMax > 0 && combined.length > listMax) {
                setItems(combined.slice(0, listMax));
                updateValue(
                  rowIndex,
                  columnIndex,
                  JSON.stringify(combined.slice(0, listMax)),
                  false
                );
              } else {
                setItems(combined);
                updateValue(
                  rowIndex,
                  columnIndex,
                  JSON.stringify(combined),
                  false
                );
              }
            }
            setInputValue(parts[parts.length - 1]);
          } else {
            setInputValue(newInputValue);
          }
        }}
        disabled={isReadOnly}
        size={size || 'small'}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={option}
              size="small"
              sx={{
                backgroundColor: getColourFromString(option),
                color: '#fff',
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    color: '#fff',
                  },
                },
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={items.length === 0 ? 'Type and press Enter' : ''}
          />
        )}
      />
      {constraintMessage && (
        <ConstraintText>{constraintMessage}</ConstraintText>
      )}
    </Box>
  );
}

export default ListInput;
